/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { Button, ITelemetryContent, getPayloadObject, getTelemetryAttributes } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import classnames from 'classnames';

/**
 * Class names used in component.
 */
const componentClassName = 'ms-order-history-with-search-and-filters-organization-wide-selector';
const buttonClassName = `${componentClassName}__button`;
const buttonActiveClassName = `${componentClassName}__active-button`;
const buttonInactiveClassName = `${componentClassName}__inactive-button`;

/**
 * IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorResources interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorResources {
    currentUserOptionLabel: string;
    currentUserOptionAriaLabel?: string;
    organizationWideOptionLabel: string;
    organizationWideOptionAriaLabel?: string;
}

/**
 * OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState interface.
 */
export enum OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState {
    CurrentUser,
    OrganizationWide
}

/**
 * IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorData interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorData {
    customer: Customer;
}

/**
 * IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps
    extends IComponentProps<IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorData> {
    className?: string;
    resources: IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorResources;
    currentSelectorState: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState;
    telemetryContent?: ITelemetryContent;
    onChange(state: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState): void;
}

/**
 * OrderHistoryWithSearchAndFiltersOrganizationWideSelector component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderHistoryWithSearchAndFiltersOrganizationWideSelectorFunctionalComponent: React.FC<IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps> = (
    props: IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps
) => {
    // Current version doesn't include functionality for non b2b users or non-admin b2b users.
    if (!props.data.customer.IsB2bAdmin) {
        return <div className={classnames(props.className, componentClassName)} />;
    }

    const [selectorState, setSelectorState] = React.useState(props.currentSelectorState);

    const onClickHandler = (newSelectorState: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState) => {
        setSelectorState(newSelectorState);
        props.onChange(newSelectorState);
    };

    // define telemetry attributes
    const currentUserOrderHistoryAttributes = getTelemetryAttributes(
        props.telemetryContent!,
        getPayloadObject('click', props.telemetryContent!, props.resources?.currentUserOptionLabel ?? 'Current user order history')
    );
    const orgWideOrderHistoryAttributes = getTelemetryAttributes(
        props.telemetryContent!,
        getPayloadObject('click', props.telemetryContent!, props.resources?.currentUserOptionLabel ?? 'Org wide order history')
    );

    return (
        <div className={classnames(props.className, componentClassName)}>
            <Button
                className={classnames(
                    buttonClassName,
                    selectorState === OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.CurrentUser
                        ? buttonActiveClassName
                        : buttonInactiveClassName
                )}
                aria-label={props.resources?.currentUserOptionAriaLabel ?? props.resources?.currentUserOptionLabel}
                title={props.resources?.currentUserOptionAriaLabel ?? props.resources?.currentUserOptionLabel}
                onClick={() => onClickHandler(OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.CurrentUser)}
                {...currentUserOrderHistoryAttributes}
            >
                {props.resources?.currentUserOptionLabel}
            </Button>
            <Button
                className={classnames(
                    buttonClassName,
                    selectorState === OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.OrganizationWide
                        ? buttonActiveClassName
                        : buttonInactiveClassName
                )}
                aria-label={props.resources?.organizationWideOptionAriaLabel ?? props.resources?.organizationWideOptionLabel}
                title={props.resources?.organizationWideOptionAriaLabel ?? props.resources?.organizationWideOptionLabel}
                onClick={() => onClickHandler(OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.OrganizationWide)}
                {...orgWideOrderHistoryAttributes}
            >
                {props.resources?.organizationWideOptionLabel}
            </Button>
        </div>
    );
};

/**
 * OrderHistoryWithSearchAndFiltersOrganizationWideSelector component.
 */
export const OrderHistoryWithSearchAndFiltersOrganizationWideSelectorComponent: React.FunctionComponent<IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps
>('OrderHistoryWithSearchAndFiltersOrganizationWideSelectorComponent', {
    component: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorFunctionalComponent
});
